import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { LINKS } from '../lib/links'
import { orgSchema, breadcrumbSchema } from '../lib/seo'
import SeoHead from '../components/SeoHead'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

export default function AboutPage() {
  const { t } = useT()
  const locale = useLocale()

  const NUMBERS = [
    { value: t('about.number1Value'), label: t('about.number1Label') },
    { value: t('about.number2Value'), label: t('about.number2Label') },
    { value: t('about.number3Value'), label: t('about.number3Label') },
    { value: t('about.number4Value'), label: t('about.number4Label') },
  ]

  const STEPS = [
    { n: '01', title: t('about.step1Title'), desc: t('about.step1Desc') },
    { n: '02', title: t('about.step2Title'), desc: t('about.step2Desc') },
    { n: '03', title: t('about.step3Title'), desc: t('about.step3Desc') },
    { n: '04', title: t('about.step4Title'), desc: t('about.step4Desc') },
  ]

  const VALUES = [
    { title: t('about.value1Title'), desc: t('about.value1Desc') },
    { title: t('about.value2Title'), desc: t('about.value2Desc') },
    { title: t('about.value3Title'), desc: t('about.value3Desc') },
  ]

  return (
    <>
    <SeoHead
      title={t('seo.about.title')}
      description={t('seo.about.description')}
      path={localizedPath('about', locale)}
      jsonLd={[
        {
          '@type': 'AboutPage',
          '@id': 'https://yaslan.be/about#aboutpage',
          name: 'À propos de Yaslan',
          url: 'https://yaslan.be/about',
          inLanguage: locale,
          mainEntity: { '@id': 'https://yaslan.be/#organization' },
        },
        orgSchema(locale),
        breadcrumbSchema([
          { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
          { name: t('breadcrumb.about'), path: localizedPath('about', locale) },
        ]),
      ]}
    />
    <main className="relative pt-28 sm:pt-32 overflow-hidden isolate">
      {/* Background DA */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-[1100px] -z-10 pointer-events-none">
        <div
          className="absolute inset-x-0 -top-20 h-[600px]"
          style={{
            background:
              'radial-gradient(ellipse at top, rgba(59,93,255,0.22), rgba(59,93,255,0.06) 45%, transparent 75%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 75%)',
          }}
        />
      </div>

      {/* ───────────────── HERO ───────────────── */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-xs sm:text-sm text-ink-500"
        >
          <Link to={localizedPath('home', locale)} className="hover:text-accent-700 transition-colors">
            {t('about.breadcrumbHome')}
          </Link>
          <span className="mx-2 text-ink-300">/</span>
          <span className="text-ink-700">{t('about.breadcrumbCurrent')}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mt-6 sm:mt-8 inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
          </span>
          <span className="font-medium">{t('about.heroBadge')}</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="mt-5 sm:mt-6 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(2.4rem,6.5vw,4.6rem)] leading-[1.02] max-w-4xl"
        >
          {t('about.heroTitlePart1')}{' '}
          <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
            {t('about.heroTitleHighlight')}
          </span>
          .
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-5 sm:mt-6 text-base sm:text-lg text-ink-600 leading-relaxed max-w-2xl"
        >
          {t('about.heroSubtitle')}
        </motion.p>
      </section>

      {/* ───────────────── IMPACT ZONE 1 — NUMBERS ───────────────── */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-6 mt-16 sm:mt-24">
        <div className="relative rounded-2xl sm:rounded-3xl border border-ink-100 bg-white/80 backdrop-blur-sm overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.4]"
            style={{
              backgroundImage:
                'linear-gradient(to right, rgba(59,93,255,0.06) 1px, transparent 1px)',
              backgroundSize: '25% 100%',
            }}
          />
          <div className="relative grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-ink-100">
            {NUMBERS.map((n, i) => (
              <motion.div
                key={n.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="relative p-6 sm:p-8 md:p-10 group"
              >
                <div className="font-semibold tracking-[-0.04em] leading-none text-[clamp(2.4rem,5vw,3.6rem)] bg-gradient-to-br from-ink-950 via-accent-700 to-accent-500 bg-clip-text text-transparent">
                  {n.value}
                </div>
                <div className="mt-3 text-[11px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-ink-500">
                  {n.label}
                </div>
                <span
                  aria-hidden
                  className="absolute right-4 top-4 w-1.5 h-1.5 rounded-full bg-accent-500 opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── IMPACT ZONE 2 — CONVICTION ───────────────── */}
      <section className="relative mt-28 sm:mt-40">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(59,93,255,0.18), transparent 65%)',
          }}
        />
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[480px] h-[480px] rounded-full blur-3xl -z-10"
          style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)' }}
        />
        <div className="max-w-5xl mx-auto px-5 sm:px-6 text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] uppercase text-accent-700"
          >
            {t('about.convictionLabel')}
          </motion.span>
          <motion.blockquote
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="mt-5 sm:mt-7 font-semibold tracking-[-0.025em] text-ink-950 text-[clamp(1.9rem,5vw,3.8rem)] leading-[1.1]"
          >
            «&nbsp;{t('about.convictionQuotePart1')}{' '}
            <span className="italic bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent">
              {t('about.convictionQuoteHighlight1')}
            </span>
            ,
            <br className="hidden sm:block" /> {t('about.convictionQuotePart2')}{' '}
            <span className="italic bg-gradient-to-r from-accent-700 via-accent-600 to-accent-500 bg-clip-text text-transparent">
              {t('about.convictionQuoteHighlight2')}
            </span>
            .&nbsp;»
          </motion.blockquote>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-6 sm:mt-8 text-sm text-ink-500"
          >
            {t('about.convictionAuthor')}
          </motion.p>
        </div>
      </section>

      {/* ───────────────── STORY — narrative ───────────────── */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-6 mt-28 sm:mt-40">
        <div className="grid lg:grid-cols-[280px_1fr] gap-10 lg:gap-16">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
              </span>
              <span className="font-medium">{t('about.storyBadge')}</span>
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              className="mt-4 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.7rem,3.5vw,2.4rem)] leading-[1.1]"
            >
              {t('about.storyTitlePart1')}{' '}
              <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.05em] inline-block">
                {t('about.storyTitleHighlight')}
              </span>
              .
            </motion.h2>
          </div>

          <div className="relative">
            <motion.div
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 top-1 bottom-1 w-px bg-gradient-to-b from-accent-500 via-accent-300 to-transparent origin-top hidden md:block"
            />
            <div className="md:pl-10 space-y-6">
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7 }}
                className="text-lg sm:text-xl text-ink-700 leading-relaxed first-letter:text-5xl sm:first-letter:text-6xl first-letter:font-bold first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85] first-letter:text-accent-700"
              >
                {t('about.storyParagraph1')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="relative rounded-xl bg-accent-50/60 border-l-2 border-accent-500 px-6 py-5"
              >
                <p className="font-semibold text-xl sm:text-2xl text-ink-950 leading-snug">
                  «&nbsp;{t('about.storyQuotePart1')} <span className="bg-gradient-to-r from-accent-600 to-accent-500 bg-clip-text text-transparent font-semibold">{t('about.storyQuoteHighlight')}</span> {t('about.storyQuotePart2')}&nbsp;»
                </p>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="text-base sm:text-lg text-ink-600 leading-relaxed"
              >
                {t('about.storyParagraph2Part1')} <span className="text-ink-950 font-medium">{t('about.storyParagraph2Highlight')}</span>
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── IMPACT ZONE 3 — METHOD timeline ───────────────── */}
      <section className="relative mt-28 sm:mt-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
            </span>
            <span className="font-medium">{t('about.methodBadge')}</span>
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="mt-5 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.9rem,4.4vw,3.2rem)] leading-[1.05] max-w-3xl"
          >
            {t('about.methodTitlePart1')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {t('about.methodTitleHighlight')}
            </span>
            .
          </motion.h2>

          <div className="relative mt-12 sm:mt-16">
            {/* horizontal connector — desktop */}
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              className="hidden md:block absolute top-7 left-[12.5%] right-[12.5%] h-px origin-left"
              style={{
                backgroundImage:
                  'linear-gradient(to right, rgba(59,93,255,0.6) 0%, rgba(59,93,255,0.3) 50%, rgba(59,93,255,0.6) 100%)',
              }}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-6">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className="relative flex flex-col items-start md:items-center md:text-center"
                >
                  <div className="relative z-10 w-14 h-14 rounded-full bg-ink-100 border border-accent-200 flex items-center justify-center shadow-[0_10px_30px_-12px_rgba(59,93,255,0.4)]">
                    <span className="font-mono text-sm font-semibold bg-gradient-to-br from-accent-600 to-accent-700 bg-clip-text text-transparent">
                      {s.n}
                    </span>
                    <motion.span
                      aria-hidden
                      animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0, 0.6] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
                      className="absolute inset-0 rounded-full border border-accent-400"
                    />
                  </div>
                  <h3 className="mt-5 text-lg sm:text-xl font-semibold text-ink-950 tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-ink-600 leading-relaxed md:max-w-[18ch]">
                    {s.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── VALUES ───────────────── */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-6 mt-28 sm:mt-40">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
          </span>
          <span className="font-medium">{t('about.valuesBadge')}</span>
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="mt-5 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.9rem,4.4vw,3.2rem)] leading-[1.05] max-w-3xl"
        >
          {t('about.valuesTitlePart1')}{' '}
          <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
            {t('about.valuesTitleHighlight')}
          </span>
          .
        </motion.h2>

        <div className="mt-10 sm:mt-12 grid sm:grid-cols-3 gap-4 sm:gap-5">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-2xl border border-ink-100 bg-ink-100 p-6 hover:border-accent-200 hover:shadow-[0_20px_50px_-22px_rgba(59,93,255,0.4)] transition-all"
            >
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-accent-600 to-accent-700 text-white flex items-center justify-center shadow-[0_10px_24px_-10px_rgba(59,93,255,0.7)]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-ink-950 tracking-tight">
                {v.title}
              </h3>
              <p className="mt-1 text-ink-600 leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ───────────────── IMPACT ZONE 4 — FINAL CTA ───────────────── */}
      <section className="relative mt-28 sm:mt-40 pb-24 sm:pb-32">
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-3xl bg-gradient-to-br from-ink-950 via-ink-900 to-ink-950 text-white px-6 sm:px-12 py-16 sm:py-24 overflow-hidden isolate"
          >
            {/* dot pattern */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-30 -z-10"
              style={{
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
                maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
              }}
            />
            {/* big halos */}
            <motion.div
              aria-hidden
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -right-32 -top-32 w-[460px] h-[460px] rounded-full blur-3xl -z-10"
              style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.55), transparent 70%)' }}
            />
            <motion.div
              aria-hidden
              animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
              className="absolute -left-32 -bottom-32 w-[380px] h-[380px] rounded-full blur-3xl -z-10"
              style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.35), transparent 70%)' }}
            />
            {/* orbiting particle */}
            <motion.div
              aria-hidden
              animate={{ rotate: 360 }}
              transition={{ duration: 24, repeat: Infinity, ease: 'linear' }}
              className="absolute left-1/2 top-1/2 w-[600px] h-[600px] -translate-x-1/2 -translate-y-1/2 -z-10"
            >
              <span className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-accent-400/80 blur-[2px]" />
              <span className="absolute right-[12%] top-[18%] w-1.5 h-1.5 rounded-full bg-accent-300/70 blur-[2px]" />
            </motion.div>

            <div className="relative max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm px-3 py-1 text-xs font-semibold text-accent-200">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-70 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
                </span>
                {t('about.ctaBadge')}
              </span>

              <h3 className="mt-6 font-semibold tracking-[-0.035em] text-[clamp(2.4rem,6vw,4.4rem)] leading-[1.02]">
                {t('about.ctaTitlePart1')}{' '}
                <span className="font-semibold bg-gradient-to-r from-accent-300 via-accent-200 to-white bg-clip-text text-transparent pb-[0.08em] inline-block">
                  {t('about.ctaTitleHighlight')}
                </span>
                <br className="hidden sm:block" /> {t('about.ctaTitlePart2')}
              </h3>

              <p className="mt-5 sm:mt-6 text-base sm:text-lg text-ink-300 leading-relaxed max-w-xl">
                {t('about.ctaSubtitle')}
              </p>

              <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
                <Link
                  to={`${localizedPath('contact', locale)}#rendez-vous`}
                  className="relative group inline-flex items-center gap-2.5 bg-ink-100 hover:bg-accent-50 text-ink-950 rounded-full pl-6 pr-5 py-3.5 sm:py-4 text-sm sm:text-base font-semibold shadow-[0_24px_60px_-18px_rgba(59,93,255,0.7)] transition-colors overflow-hidden"
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-accent-200/60 to-transparent pointer-events-none"
                  />
                  <span className="relative">{t('about.ctaPrimary')}</span>
                  <span className="relative flex items-center justify-center w-6 h-6 rounded-full bg-ink-950 text-white">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </Link>
                <Link
                  to={localizedPath('home', locale)}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-5 py-3.5 sm:py-4 text-sm sm:text-base font-medium text-white/90 hover:bg-white/10 hover:border-white/30 transition-colors"
                >
                  {t('about.ctaSecondary')}
                </Link>
              </div>

              <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs sm:text-sm text-ink-400">
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {t('about.ctaTrust1')}
                </span>
                <span className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-accent-400">
                    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('about.ctaTrust2')}
                </span>
                <span className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-accent-400">
                    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('about.ctaTrust3')}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
    </>
  )
}
